import React from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'react-lazyload'
import Link from 'gatsby-link'
import withStyles from '@material-ui/core/styles/withStyles'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CircularProgress from '@material-ui/core/CircularProgress'
import Fade from '@material-ui/core/Fade'
import { noTextOverflow } from 'utils/styleUtils'
import formatCategory from 'utils/formatCategory'
import formatDate from 'utils/formatDate'
import getRandomColor from 'utils/getRandomColor'

const styles = (theme) => ({
  circularProgress: {
    display: 'block',
    margin: 'auto',
    minHeight: 195,
    [theme.breakpoints.down('md')]: {
      minHeight: 182,
    },
  },
  container: {
    marginBottom: 40,
    overflow: 'hidden',
    height: 195,
    [theme.breakpoints.down('md')]: {
      height: 182,
    },
    [theme.breakpoints.down('xs')]: {
      border: `1px solid ${theme.palette.borders.cloud}`,
      height: 'auto',
      marginBottom: 15,
    },
    '&:hover': {
      '& img': {
        transform: 'scale(1.3) rotate(-10deg)',
      },
    },
  },
  thumbnailWrapped: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      height: 190,
    },
  },
  thumbnail: {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s, opacity 1s ease-in !important',
  },
  contentWrapper: {
    height: '100%',
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9em',
    },
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  title: {
    fontWeight: 500,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.25em',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1em',
    },
  },
  meta: {
    color: theme.palette.text.soft,
    fontStyle: 'italic',
    fontWeight: 300,
    opacity: 0.8,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
      position: 'relative',
      height: 18,
    },
  },
  metaCategory: {
    color: theme.palette.accent.noticeLight,
    fontWeight: 500,
    fontStyle: 'normal',
    opacity: 0.8,
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.9em',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
    },
  },
  metaTimesWrapper: {
    float: 'right',
    [theme.breakpoints.down('xs')]: {
      float: 'none',
      position: 'absolute',
      right: 0,
      top: 4,
    },
  },
  metaTimes: {
    float: 'right',
    color: theme.palette.text.light,
    [theme.breakpoints.down('md')]: {
      fontSize: '0.85em',
    },
    [theme.breakpoints.down('xs')]: {
      // display: 'block',
      fontSize: '0.9em',
      marginTop: -5,
      float: 'none',
      fontStyle: 'normal',
      textAlign: 'right',
    },
  },
  excerpt: {
    color: theme.palette.text.main,
    lineHeight: 1.8,
    fontWeight: 300,
    fontSize: '1em',
    ...noTextOverflow({
      whiteSpace: 'normal',
      '&:hover': {},
    }),
    [theme.breakpoints.down('md')]: {
      maxHeight: 75,
      fontSize: '0.9em',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8em',
      lineHeight: 1.5,
      maxHeight: 85,
      marginTop: 5,
    },
  },
})

const Article = ({
  index,
  id,
  classes,
  slug,
  timeToRead,
  title,
  uiTitle,
  date,
  src,
  excerpt,
  category,
  pageType,
}) => {
  const rootProps = {}
  if (id) {
    rootProps.id = id
  }

  const pageProps = {}
  const categoryProps = {}

  if (pageType === 'review') {
    pageProps.title = uiTitle
    pageProps.category = 'Review'
  } else {
    pageProps.title = title
    pageProps.category = category
    categoryProps.component = Link
    categoryProps.to = `/${category}`
  }

  const Image = (props) => <img {...props} /> // Fade passes in mandatory style prop

  const color = getRandomColor()

  return (
    <Grid
      {...rootProps}
      classes={{ container: classes.container }}
      spacing={0}
      container
    >
      <Grid xs={12} sm={4} item>
        <Link to={slug}>
          {src && (
            <div className={classes.thumbnailWrapped}>
              <LazyLoad
                placeholder={
                  <CircularProgress
                    className={classes.circularProgress}
                    style={{ color }}
                    size={150}
                    thickness={1}
                  />
                }
                once
              >
                <Fade timeout={2000} in>
                  <Image
                    src={src}
                    className={classes.thumbnail}
                    alt={pageProps.title}
                    title={pageProps.title}
                  />
                </Fade>
              </LazyLoad>
            </div>
          )}
        </Link>
      </Grid>
      <Grid xs={12} sm={8} item>
        <Card className={classes.contentWrapper}>
          <CardContent>
            <Typography
              variant="headline"
              classes={{ root: classes.title }}
              component={Link}
              to={slug}
              gutterBottom
            >
              {pageProps.title}
            </Typography>
            <div className={classes.meta}>
              <Typography
                {...categoryProps}
                variant="subheading"
                classes={{ root: classes.metaCategory }}
              >
                {pageType === 'review' ? 'Review' : formatCategory(category)}{' '}
              </Typography>{' '}
              <div className={classes.metaTimesWrapper}>
                <Typography classes={{ root: classes.metaTimes }}>
                  {formatDate(date, 'MMMM Do')} {'\u2022'} {timeToRead} min read
                </Typography>
              </div>
            </div>

            <Typography classes={{ root: classes.excerpt }}>
              {excerpt}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

Article.propTypes = {
  classes: PropTypes.object.isRequired,
  index: PropTypes.number,
  slug: PropTypes.string.isRequired,
  timeToRead: PropTypes.number.isRequired,
  excerpt: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  uiTitle: PropTypes.string,
  date: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  category: PropTypes.string,
  pageType: PropTypes.string,
}

export default withStyles(styles)(Article)



// WEBPACK FOOTER //
// ./src/components/ArticleItem.js